import * as React from "react";

import { ssnDigitsOnlyIsValid, stripSSNDashes, addSSNDashes } from "@eave/shared/utils/ssn";

import { Modal, Button, TextInput } from "carbon-components-react";

import APIClient from "web_modules/APIClient";
import { BorrowerEntity, LoanApplicationBorrowers } from "@eave/shared/types/api";

interface SSNEditModalButtonProps {
  appID: number;
  borrowers: LoanApplicationBorrowers;
}

interface GetSSNsResponse {
  borrower1_ssn: string;
  borrower2_ssn: string;
  borrower3_ssn: string;
  borrower4_ssn: string;
}

enum curStates {
  Start = "start",
  Submitting = "submitting",
  End = "end",
}

const SSNEditModalButton: React.FC<SSNEditModalButtonProps> = (props: SSNEditModalButtonProps) => {
  const { appID, borrowers } = props;

  const [modalIsOpen, setModalIsOpen] = React.useState<boolean>(false);

  const [originalBorrower1SSN, setOriginalBorrower1SSN] = React.useState<string>(undefined);
  const [borrower1SSN, setBorrower1SSN] = React.useState<string>(undefined);
  const [originalBorrower2SSN, setOriginalBorrower2SSN] = React.useState<string>(undefined);
  const [borrower2SSN, setBorrower2SSN] = React.useState<string>(undefined);
  const [originalBorrower3SSN, setOriginalBorrower3SSN] = React.useState<string>(undefined);
  const [borrower3SSN, setBorrower3SSN] = React.useState<string>(undefined);
  const [originalBorrower4SSN, setOriginalBorrower4SSN] = React.useState<string>(undefined);
  const [borrower4SSN, setBorrower4SSN] = React.useState<string>(undefined);

  const [curState, setCurState] = React.useState<string>(curStates.Start);
  const [curStateHeader, setCurStateHeader] = React.useState<string>(null);
  const [curStateMsg, setCurStateMsg] = React.useState<string>(null);
  const [redirect, setRedirect] = React.useState<string>(null);

  const hasBorrower1 = borrowers.hasOwnProperty(BorrowerEntity.Borrower1);
  const hasBorrower2 = borrowers.hasOwnProperty(BorrowerEntity.Borrower2);
  const hasBorrower3 = borrowers.hasOwnProperty(BorrowerEntity.Borrower3);
  const hasBorrower4 = borrowers.hasOwnProperty(BorrowerEntity.Borrower4);

  React.useEffect(() => {
    getSSNs();
  }, [appID]);

  const getSSNs = () =>
    APIClient.get(`/uw/loan_applications/${appID}/ssn`).then((resp: GetSSNsResponse) => {
      if (resp.borrower1_ssn) {
        const borrower1SSN = stripSSNDashes(resp.borrower1_ssn);
        setOriginalBorrower1SSN(borrower1SSN);
        setBorrower1SSN(borrower1SSN);
      }
      if (resp.borrower2_ssn) {
        const borrower2SSN = stripSSNDashes(resp.borrower2_ssn);
        setOriginalBorrower2SSN(borrower2SSN);
        setBorrower2SSN(borrower2SSN);
      }
      if (resp.borrower3_ssn) {
        const borrower3SSN = stripSSNDashes(resp.borrower3_ssn);
        setOriginalBorrower3SSN(borrower3SSN);
        setBorrower3SSN(borrower3SSN);
      }
      if (resp.borrower4_ssn) {
        const borrower4SSN = stripSSNDashes(resp.borrower4_ssn);
        setOriginalBorrower4SSN(borrower4SSN);
        setBorrower4SSN(borrower4SSN);
      }
    });

  const atLeastOneSSNChanged = () =>
    (hasBorrower1 && borrower1SSN !== originalBorrower1SSN) ||
    (hasBorrower2 && borrower2SSN !== originalBorrower2SSN) ||
    (hasBorrower3 && borrower3SSN !== originalBorrower3SSN) ||
    (hasBorrower4 && borrower4SSN !== originalBorrower4SSN);

  const setSSNs = () => {
    setCurState(curStates.Submitting);

    if (!atLeastOneSSNChanged()) {
      setCurState(curStates.End);
      setCurStateHeader("No SSNs changed");
      return;
    }

    const payload = {
      borrowers: [
        hasBorrower1 &&
          borrower1SSN !== originalBorrower1SSN && {
            borrower: BorrowerEntity.Borrower1,
            ssn: addSSNDashes(borrower1SSN),
          },
        hasBorrower2 &&
          borrower2SSN !== originalBorrower2SSN && {
            borrower: BorrowerEntity.Borrower2,
            ssn: addSSNDashes(borrower2SSN),
          },
        hasBorrower3 &&
          borrower3SSN !== originalBorrower3SSN && {
            borrower: BorrowerEntity.Borrower3,
            ssn: addSSNDashes(borrower3SSN),
          },
        hasBorrower4 &&
          borrower4SSN !== originalBorrower4SSN && {
            borrower: BorrowerEntity.Borrower4,
            ssn: addSSNDashes(borrower4SSN),
          },
      ].filter(Boolean),
    };

    const stripLine = (errorString: string) => {
      // matches e.g. "eave-go/handlers/social_security_numbers.go:156: "
      const lineR = /([a-zA-Z0-9()-_]*\/)*([a-zA-Z0-9()-_]*).go:[0-9]*: /;
      const lineInfo = errorString.match(lineR);
      if (lineInfo.length > 0) {
        return errorString.replace(lineInfo[0], "");
      }
      return errorString;
    };

    const getURLFromString = (errorString: string) => {
      const urlR =
        /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}(\.[a-zA-Z0-9()]{1,6}\b)?([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
      const url = errorString.match(urlR);
      return url;
    };

    APIClient.post(`/uw/loan_applications/${appID}/ssn`, payload)
      .then(() => {
        setCurState(curStates.End);
        setCurStateHeader("Success!");

        // any SSNs that needed to be updated, were -- so reset
        setOriginalBorrower1SSN(borrower1SSN);
        setOriginalBorrower2SSN(borrower2SSN);
        setOriginalBorrower3SSN(borrower3SSN);
        setOriginalBorrower4SSN(borrower4SSN);
      })
      .catch(({ meta }) => {
        setCurState(curStates.End);
        const { error_detail } = meta;
        setCurStateHeader("Error!");
        const errorMessage = stripLine(error_detail);
        setCurStateMsg(errorMessage);
        const urlMatches = getURLFromString(errorMessage);
        if (urlMatches.length > 0) {
          setRedirect(urlMatches[0]);
        }
      });
  };

  const invalidSSNText = "Please enter 9 digits only (no dashes)";

  return (
    <>
      <Button onClick={() => setModalIsOpen(true)}>Edit SSNs</Button>
      <Modal
        open={modalIsOpen}
        primaryButtonText="Save"
        primaryButtonDisabled={
          curState !== curStates.Start ||
          (hasBorrower1 && !ssnDigitsOnlyIsValid(borrower1SSN)) ||
          (hasBorrower2 && !ssnDigitsOnlyIsValid(borrower2SSN)) ||
          (hasBorrower3 && !ssnDigitsOnlyIsValid(borrower3SSN)) ||
          (hasBorrower4 && !ssnDigitsOnlyIsValid(borrower4SSN))
        }
        onRequestClose={() => {
          setModalIsOpen(false);
          setCurStateHeader(null);
          setCurStateMsg(null);
          setCurState(curStates.Start);
        }}
        onRequestSubmit={() => {
          setSSNs();
          return true;
        }}
        secondaryButtonText="Cancel"
      >
        {curStateHeader && <h1>{curStateHeader}</h1>}
        {curStateMsg && <p>{curStateMsg}</p>}
        {redirect && <a href={redirect}>{redirect}</a>}
        {curState === curStates.Start && (
          <>
            {hasBorrower1 && (
              <>
                <TextInput
                  id="borrower1"
                  labelText="Borrower 1 SSN"
                  defaultValue={borrower1SSN}
                  invalid={!ssnDigitsOnlyIsValid(borrower1SSN)}
                  invalidText={invalidSSNText}
                  onChange={e => setBorrower1SSN(e.target.value)}
                />
              </>
            )}
            {hasBorrower2 && (
              <>
                <div style={{ height: 20 }} />
                <TextInput
                  id="borrower2"
                  labelText="Borrower 2 SSN"
                  defaultValue={borrower2SSN}
                  invalid={!ssnDigitsOnlyIsValid(borrower2SSN)}
                  invalidText={invalidSSNText}
                  onChange={e => setBorrower2SSN(e.target.value)}
                />
              </>
            )}
            {hasBorrower3 && (
              <>
                <div style={{ height: 20 }} />
                <TextInput
                  id="borrower3"
                  labelText="Borrower 3 SSN"
                  defaultValue={borrower3SSN}
                  invalid={!ssnDigitsOnlyIsValid(borrower3SSN)}
                  invalidText={invalidSSNText}
                  onChange={e => setBorrower3SSN(e.target.value)}
                />
              </>
            )}
            {hasBorrower4 && (
              <>
                <div style={{ height: 20 }} />
                <TextInput
                  id="borrower4"
                  labelText="Borrower 4 SSN"
                  defaultValue={borrower4SSN}
                  invalid={!ssnDigitsOnlyIsValid(borrower4SSN)}
                  invalidText={invalidSSNText}
                  onChange={e => setBorrower4SSN(e.target.value)}
                />
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default SSNEditModalButton;
